import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { Plus, CheckCircle, AlertCircle, Clock, Unlock, Edit, RefreshCw, Settings } from 'lucide-react';
import { toast } from 'sonner';
import InitialSetup from './InitialSetup';

import { BACKEND_URL } from '@/lib/api';
const API = `${BACKEND_URL}/api`;

const CashierDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [todayClosing, setTodayClosing] = useState(null);
  const [unlockedClosings, setUnlockedClosings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [needsInitialSetup, setNeedsInitialSetup] = useState(false);
  const today = new Date().toISOString().split('T')[0];

  const checkInitialSetup = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dayclosings/previous-closing-cash`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNeedsInitialSetup(response.data.needs_initial_setup === true);
    } catch (error) {
      console.error('Failed to check initial setup');
    }
  };

  const fetchClosings = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all cashier's closings (last 90 days to include any unlocked older closings)
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      const startDate = ninetyDaysAgo.toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/dayclosings`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: startDate, end_date: today }
      });
      
      // Separate today's closing from unlocked previous closings
      const closings = response.data;
      const todaysClosing = closings.find(c => c.date === today);
      const previousUnlocked = closings.filter(c => 
        c.date !== today && c.status === 'draft'
      );
      
      setTodayClosing(todaysClosing || null);
      setUnlockedClosings(previousUnlocked);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to fetch closings');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [today]);

  // Initial load
  useEffect(() => {
    checkInitialSetup();
    fetchClosings();
  }, [fetchClosings]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchClosings(false);
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchClosings]);

  const handleManualRefresh = () => {
    fetchClosings(false);
    toast.success('Data refreshed!');
  };

  const handleSetupComplete = () => {
    setNeedsInitialSetup(false);
    toast.success('Initial setup complete! You can now create day closings.');
  };

  const handleCreateClosing = () => {
    navigate('/day-closing');
  };

  const handleEditClosing = (closingId) => {
    navigate(`/day-closing/${closingId || todayClosing.id}`);
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  // Show initial setup notice for cashier (admin does the setup)
  if (needsInitialSetup) {
    return (
      <div className="space-y-6">
        <Card className="border-2 border-yellow-200 bg-yellow-50 max-w-xl mx-auto mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <AlertCircle className="w-6 h-6" />
              Initial Setup Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-yellow-700">
              The opening cash and bank balances have not been set up yet. 
              Please contact your <strong>Admin</strong> to complete the initial setup before you can start creating day closings.
            </p>
            <p className="text-sm text-yellow-600 mt-4">
              Admin can set this up from: <strong>Settings → Initial Setup</strong>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cashier-dashboard">
      {/* Refresh Bar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Last updated: {lastRefresh.toLocaleTimeString('en-IN')}
          {refreshing && <span className="ml-2 text-blue-500">• Refreshing...</span>}
        </p>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleManualRefresh}
          disabled={refreshing}
          className="gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Unlocked Previous Day Closings Alert */}
      {unlockedClosings.length > 0 && (
        <Card className="border-l-4 border-l-orange-500 bg-orange-50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-orange-800" style={{ fontFamily: 'Space Grotesk' }}>
              <Unlock className="w-5 h-5" />
              Unlocked Day Closings - Action Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-orange-700 mb-4">
              Admin has unlocked the following day closings for you to edit and resubmit:
            </p>
            <div className="space-y-3">
              {unlockedClosings.map((closing) => (
                <div 
                  key={closing.id} 
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-200"
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {new Date(closing.date).toLocaleDateString('en-IN', {
                          weekday: 'short',
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                      <p className="text-sm text-gray-600">
                        Sales: ₹{closing.total_sales?.toLocaleString('en-IN')} | 
                        Cash: ₹{closing.closing_cash?.toLocaleString('en-IN')}
                      </p>
                    </div>
                    <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                      <Unlock className="w-3 h-3 mr-1" />
                      Unlocked
                    </Badge>
                  </div>
                  <Button 
                    onClick={() => handleEditClosing(closing.id)}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid={`edit-unlocked-${closing.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit & Resubmit
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's Day Closing Card */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Space Grotesk' }}>Today's Day Closing</CardTitle>
        </CardHeader>
        <CardContent>
          {!todayClosing ? (
            <div className="text-center py-8">
              <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No day closing created for today yet.</p>
              <Button onClick={handleCreateClosing} size="lg" data-testid="create-closing-btn">
                <Plus className="w-5 h-5 mr-2" />
                Create Today's Day Closing
              </Button>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <div className="flex items-center mt-1">
                    {todayClosing.status === 'draft' && (
                      <>
                        <Clock className="w-5 h-5 text-yellow-500 mr-2" />
                        <span className="text-lg font-semibold text-yellow-700">Draft</span>
                      </>
                    )}
                    {todayClosing.status === 'closed' && (
                      <>
                        <CheckCircle className="w-5 h-5 text-blue-500 mr-2" />
                        <span className="text-lg font-semibold text-blue-700">Submitted</span>
                      </>
                    )}
                    {todayClosing.status === 'approved' && (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                        <span className="text-lg font-semibold text-green-700">Approved</span>
                      </>
                    )}
                  </div>
                </div>
                {todayClosing.status === 'draft' && (
                  <Button onClick={() => handleEditClosing()} data-testid="edit-closing-btn">
                    Edit Day Closing
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Sales</p>
                  <p className="text-2xl font-bold text-green-700" style={{ fontFamily: 'Space Grotesk' }}>
                    ₹{todayClosing.total_sales?.toLocaleString('en-IN') || '0.00'}
                  </p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Deductions</p>
                  <p className="text-2xl font-bold text-red-700" style={{ fontFamily: 'Space Grotesk' }}>
                    ₹{todayClosing.total_deductions?.toLocaleString('en-IN') || '0.00'}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600">Closing Cash</p>
                  <p className="text-2xl font-bold text-blue-700" style={{ fontFamily: 'Space Grotesk' }}>
                    ₹{todayClosing.closing_cash?.toLocaleString('en-IN') || '0.00'}
                  </p>
                </div>
              </div>

              {todayClosing.notes && (
                <div className="mt-4 p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600">Notes</p>
                  <p className="text-gray-900">{todayClosing.notes}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Instructions Card */}
      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Space Grotesk' }}>Instructions</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>Create a new day closing entry at the start of your shift</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>Add all deductions (payments, deposits, expenses) throughout the day</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>Review the closing cash amount before submitting</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>Click "Close Day" to lock the entry - you won't be able to edit after closing</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>Admin will review and approve your submission</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default CashierDashboard;
